import { useTranslationApp } from "../../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../../lib/redux";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Loader from "../../../components/Loaders/Loader";
import {
  ADS_SUBSCRIPTION_DESCRIPTION,
  ADS_SUBSCRIPTION_TITLE,
  ALERT_CANCEL_SUBSCRIPTION_DESCRIPTION,
  ALERT_CANCEL_SUBSCRIPTION_TITLE,
  ALERT_REACTIVATE_SUBSCRIPTION_TITLE,
  ALERT_PAYMENT_METHOD_CHANGED_TITLE,
  BILLING_AND_PAYMENT_MODEL_PACKAGE_ACTIVATE_PACKAGE,
  BUTTON_CANCEL_SUBSCRIPTION,
  BUTTON_REACTIVATE_SUBSCRIPTION,
  BUTTON_CHANGE_PAYMENT_METHODS,
  BILLING_AND_PAYMENT_MODEL_PACKAGE_CANCEL_PACKAGE,
  ALERT_CANCEL_PLAN_TITLE,
  MODAL_PAY_SUBSCRIPTION_PENDING_PAYMENT_TITLE,
  MODAL_PAY_SUBSCRIPTION_PAY_NOW,
  BILLING_PENDING_SUBSCRIPTION_PAYMENT_DESCRIPTION,
  DATE_SHORT,
} from "../../../i18n/keysTranslations";
import { capitalizeFirstLetter } from "../../../utils/string";
import Button from "../../../components/Buttons/Button";
import { BOX_SHADOW_THEME } from "../../../utils/colors";
import { setShowModalSelectSubscription } from "../../../actions/ui";
import { getNameBusinessTheme } from "../../../actions/getters";
import {
  startCancelReason,
  startCancelSubscription,
  startReactivateSubscription,
  startCreateUpdatePaymentMethodLinkAds,
} from "../../../actions/payments";
import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { SimpleAlert } from "../../../components/Alerts/Alerts";
import { ALERT_ICON_TYPE_SUCCESS } from "../../../utils/constants";
import ModalCancelSubscription from "../../../components/Modal/ModalCancelSubscription";
import addDays from "date-fns/addDays";

const SubscriptionInformation = ({
  isPendingSubscriptionPaymentMode = false,
  pendingSubscriptionInvoice = null,
  onPayPendingSubscription = () => {},
  payPendingLoading = false,
}) => {
  const [isFetchingActionSubscription, setIsFetchingActionSubscription] =
    useState(false);
  const [isLoadingPaymentMethods, setIsLoadingPaymentMethods] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const { t } = useTranslationApp();
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();

  const { subscriptionPlan, subscriptionPlanInfo, isLoading, isPlanActive } =
    useSelectorApp((state) => state.payments);

  const dispatch = useDispatchApp();

  useEffect(() => {
    const from = params.get("from");
    const flow = params.get("flow");

    if (from === "stripe_portal" && flow === "pm_update") {
      SimpleAlert({
        title: t(ALERT_PAYMENT_METHOD_CHANGED_TITLE),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });

      params.delete("from");
      params.delete("flow");
      setParams(params);
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const reactivateSubscription = params.get("reactivateSubscription");

    if (
      reactivateSubscription === "true" &&
      subscriptionPlanInfo?.cancelDateTime
    ) {
      onReactivateSubscription();

      params.delete("reactivateSubscription");
      setParams(params);
    }
    // eslint-disable-next-line
  }, [params, subscriptionPlanInfo?.cancelDateTime]);

  const onCancelSubscription = () => {
    setShowCancellationModal(true);
  };

  const onCancelSubscriptionSubmit = async (cancellationData) => {
    setShowCancellationModal(false);
    setIsFetchingActionSubscription(true);

    try {
      const response = await dispatch(startCancelSubscription());

      if (response.ok) {
        SimpleAlert({
          title: t(
            !subscriptionPlan
              ? ALERT_CANCEL_PLAN_TITLE
              : ALERT_CANCEL_SUBSCRIPTION_TITLE
          ),
          text: !subscriptionPlan
            ? null
            : t(ALERT_CANCEL_SUBSCRIPTION_DESCRIPTION, {
                date: response?.cancelDateTime
                  ? new Date(response?.cancelDateTime)
                  : new Date(),
              }),
          icon: ALERT_ICON_TYPE_SUCCESS,
        });
        dispatch(startCancelReason(cancellationData));
        if (!subscriptionPlan) {
          dispatch(setShowModalSelectSubscription(true));
          navigate("/");
        }
      }
      setIsFetchingActionSubscription(false);
    } catch (error) {
      console.error("Error in cancellation process:", error);
      setIsFetchingActionSubscription(false);
    }
  };

  const onCloseCancellationModal = () => {
    setShowCancellationModal(false);
  };

  const onReactivateSubscription = async () => {
    setIsFetchingActionSubscription(true);
    const response = await dispatch(startReactivateSubscription());
    if (response.ok) {
      SimpleAlert({
        title: t(ALERT_REACTIVATE_SUBSCRIPTION_TITLE),
        icon: ALERT_ICON_TYPE_SUCCESS,
      });
    }
    setIsFetchingActionSubscription(false);
  };

  const onManagePaymentMethods = async () => {
    setIsLoadingPaymentMethods(true);
    const response = await dispatch(startCreateUpdatePaymentMethodLinkAds({}));
    if (response.ok && response.data?.url) {
      window.location.href = response.data.url;
    }
    setIsLoadingPaymentMethods(false);
  };

  const onGetLimitDate = () => {
    if (isPlanActive) return null;
    if (subscriptionPlanInfo?.paymentFailedLimitDate)
      return subscriptionPlanInfo?.paymentFailedLimitDate;
    if (pendingSubscriptionInvoice?.dueDate)
      return addDays(
        new Date(pendingSubscriptionInvoice?.dueDate),
        30
      ).getTime();
    return null;
  };

  const cancelDateTime = subscriptionPlanInfo?.cancelDateTime;
  const showButtonReactivate =
    cancelDateTime != null && new Date().getTime() < cancelDateTime;

  const limitDateRaw = onGetLimitDate();
  const limitDateFormatted =
    limitDateRaw != null && limitDateRaw !== ""
      ? t(DATE_SHORT, {
          date:
            limitDateRaw instanceof Date
              ? limitDateRaw
              : new Date(limitDateRaw),
        })
      : "";

  const NAME_BUSINESS = dispatch(getNameBusinessTheme());

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          py: 2,
          px: 2,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          backgroundColor: "background.paperSecondary",
          boxShadow: BOX_SHADOW_THEME,
          border: "1px solid rgba(0, 0, 0, 0.15)",
          "&:hover": {
            boxShadow: "0 8px 20px rgba(0, 0, 0, 0.08)",
          },
          borderRadius: 4,
        }}
      >
        {isLoading && <Loader />}
        {subscriptionPlan && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              height: "100%",
            }}
          >
            {isPendingSubscriptionPaymentMode ? (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {t(MODAL_PAY_SUBSCRIPTION_PENDING_PAYMENT_TITLE, {
                    value: pendingSubscriptionInvoice?.value,
                    currency: (
                      pendingSubscriptionInvoice?.currency || "USD"
                    ).toUpperCase(),
                  })}
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t(BILLING_PENDING_SUBSCRIPTION_PAYMENT_DESCRIPTION, {
                    limitDate: limitDateFormatted,
                    NAME_BUSINESS,
                  })}
                </Typography>

                <>
                  <Box
                    sx={{
                      display: "flex",
                      height: "100%",
                      width: "100%",
                      justifyContent: "flex-end",
                      flexWrap: "wrap",
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <Button
                      loading={isLoadingPaymentMethods}
                      color="secondary"
                      onClick={onManagePaymentMethods}
                      sx={{
                        alignSelf: "flex-end",
                      }}
                    >
                      {t(BUTTON_CHANGE_PAYMENT_METHODS)}
                    </Button>
                    <Button
                      loading={payPendingLoading}
                      color="primary"
                      onClick={onPayPendingSubscription}
                      sx={{
                        alignSelf: "flex-end",
                      }}
                    >
                      {t(MODAL_PAY_SUBSCRIPTION_PAY_NOW)}
                    </Button>
                  </Box>
                  {/* <Typography
                    variant="caption"
                    onClick={onCancelSubscription}
                    sx={{
                      position: "relative",
                      lineHeight: 0,
                      bottom: -32,
                      alignSelf: "flex-end",
                      cursor: "pointer",
                      color: "text.secondary",
                      textDecoration: "underline",
                      "&:hover": {
                        color: "primary.main",
                      },
                    }}
                  >
                    {t(BUTTON_CANCEL_SUBSCRIPTION)}
                  </Typography> */}
                </>
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                  {`${t(ADS_SUBSCRIPTION_TITLE, {
                    value: capitalizeFirstLetter(subscriptionPlan),
                  })} ${
                    subscriptionPlanInfo.recurrent
                      ? `(${t(subscriptionPlanInfo.recurrent)})`
                      : ""
                  }`}
                </Typography>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  {t(ADS_SUBSCRIPTION_DESCRIPTION)}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    height: "100%",
                    width: "100%",
                    justifyContent: "flex-end",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <Button
                    loading={isLoadingPaymentMethods}
                    color="secondary"
                    onClick={onManagePaymentMethods}
                    sx={{
                      alignSelf: "flex-end",
                    }}
                  >
                    {t(BUTTON_CHANGE_PAYMENT_METHODS)}
                  </Button>
                  {subscriptionPlanInfo?.cancelDateTime &&
                  showButtonReactivate ? (
                    <Button
                      loading={isFetchingActionSubscription}
                      color="secondary"
                      onClick={onReactivateSubscription}
                      sx={{
                        alignSelf: "flex-end",
                      }}
                    >
                      {t(BUTTON_REACTIVATE_SUBSCRIPTION)}
                    </Button>
                  ) : (
                    <Button
                      loading={isFetchingActionSubscription}
                      color="secondary"
                      onClick={onCancelSubscription}
                      sx={{
                        alignSelf: "flex-end",
                      }}
                    >
                      {t(BUTTON_CANCEL_SUBSCRIPTION)}
                    </Button>
                  )}
                </Box>
              </>
            )}
          </Box>
        )}
        {!subscriptionPlan && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              alignItems: "flex-start",
              height: "100%",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-end",
                gap: 1,
                width: "100%",
              }}
            >
              {isPlanActive && (
                <Button
                  loading={isFetchingActionSubscription}
                  color="secondary"
                  onClick={onCancelSubscription}
                  sx={{
                    alignSelf: "flex-end",
                  }}
                >
                  {t(BILLING_AND_PAYMENT_MODEL_PACKAGE_CANCEL_PACKAGE)}
                </Button>
              )}
              <Button
                color="primary"
                sx={{
                  width: "auto",
                  alignSelf: "flex-end",
                }}
                onClick={() => dispatch(setShowModalSelectSubscription(true))}
              >
                {t(BILLING_AND_PAYMENT_MODEL_PACKAGE_ACTIVATE_PACKAGE)}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>

      {showCancellationModal && (
        <ModalCancelSubscription
          modalOpen={showCancellationModal}
          onCloseModal={onCloseCancellationModal}
          onConfirmCancellation={onCancelSubscriptionSubmit}
          loading={isFetchingActionSubscription}
        />
      )}
    </>
  );
};

export default SubscriptionInformation;
